import React, { useCallback } from 'react'
import dayjs from 'dayjs'
import UploadIcon from '@mui/icons-material/Upload'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'
import CheckIcon from '@mui/icons-material/Check'
import { Checkbox, Chip, TableRow, Tooltip, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Warning } from '@mui/icons-material'
import { IconUser } from '@tabler/icons-react'
import EnhancedTableCell from '../../EnhancedTableCell'
import NoProvidedCustomerEmail from '../../../../../components/NoProvidedCustomerEmail'
import AdvancedMenu from '../../../../../components/TableRender/AdvancedMenu'
import classes from '../style.module.scss'
import { useSelector } from '../../../../../store/store'
import { convertValuesToLabels, fixImgSrc, hasPermission } from '../../../../../utils/tools'
import { isOrderColumnVisible } from '../../orderColumns'
import { customerTypes } from '../../../../Customers/components/CreateCustomer'

const OrdersRow = ({ labelId = '', row, isItemSelected = false, checkbox = false }) => {
  const {
    id,
    displayId,
    logoUrl,
    status,
    jobType,
    customerName,
    customerType,
    connectedReseller,
    jobName,
    place,
    createdAt,
    assignee,
    channel,
    budget,
    price,
    runtime,
    customerEmails,
    previews,
    isSelfService,
  } = row

  const navigate = useNavigate()
  const customerHasEmail = customerEmails ? JSON.parse(customerEmails)?.length : null

  const handleClick = useCallback(() => {
    navigate(`/orders/${id}`)
  }, [id, navigate])

  const { permissions, userInfo } = useSelector((state) => state.user)
  const permission = hasPermission(permissions?.orderDetailsFields)
  const { role } = userInfo

  // Shared with the table header so cells always sit under the right labels.
  const showColumn = (columnId: Parameters<typeof isOrderColumnVisible>[0]) =>
    isOrderColumnVisible(columnId, permissions?.orderDetailsFields, role)

  const TableCellOrder = (
    props: JSX.IntrinsicAttributes & {
      children: React.ReactNode
      action?: boolean | undefined
      checkbox?: boolean | undefined
      handleClick?: (() => void) | undefined
    }
  ) => {
    return (
      <EnhancedTableCell id={id} handleClick={handleClick} {...props}>
        {props.children}
      </EnhancedTableCell>
    )
  }

  const preview = previews?.length ? setPreviewStatus(status) : ''

  return (
    <TableRow
      hover
      role="checkbox"
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={id}
      selected={isItemSelected}
      sx={{ cursor: 'pointer' }}
    >
      {checkbox && (
        <TableCellOrder checkbox action>
          <Checkbox
            color="primary"
            checked={isItemSelected}
            inputProps={{
              'aria-labelledby': labelId,
            }}
          />
        </TableCellOrder>
      )}
      <TableCellOrder>
        <Typography>{displayId || 'N/A'}</Typography>
      </TableCellOrder>
      {showColumn('customerName') && (
        <>
          <TableCellOrder>
            <div className={classes.nameAndLogo}>
              <div className={classes.logo}>
                {logoUrl ? (
                  <img src={fixImgSrc(logoUrl)} alt="customer logo" width="33px" />
                ) : (
                  <IconUser width="35px" />
                )}
              </div>
              <div className={classes.textContent}>
                <Typography>
                  {customerName}
                  {!customerHasEmail && <NoProvidedCustomerEmail />}
                </Typography>
                <Typography fontSize="13px" fontStyle="italic" color="secondary">
                  {convertValuesToLabels(customerTypes, customerType)}
                </Typography>
              </div>
            </div>
          </TableCellOrder>
          {showColumn('connectedReseller') && <TableCellOrder>{connectedReseller}</TableCellOrder>}
        </>
      )}
      {showColumn('jobName') && (
        <TableCellOrder>
          <div className={classes.jobType}>
            <Typography>{jobName}</Typography>
            {isSelfService && (
              <Tooltip title="Created automatically from a job linked to the customer's own Meta campaign">
                <Chip label="Self-service" size="small" variant="outlined" sx={{ mt: 0.5 }} />
              </Tooltip>
            )}
            {permission('jobType') && (
              <Typography color="secondary" fontSize="13px" fontStyle="italic">
                {jobType}
              </Typography>
            )}
          </div>
        </TableCellOrder>
      )}
      {showColumn('status') && (
        <TableCellOrder>
          <div
            className={classes.statusOrders}
            style={{ color: status === 'unpublished' ? '#EE7202' : 'inherit' }}
          >
            {status}
            <span className={classes.space}></span>
            {status === 'unpublished' && <Warning fontSize={'small'} />}
          </div>
        </TableCellOrder>
      )}
      {showColumn('place') && <TableCellOrder>{place}</TableCellOrder>}
      {showColumn('createdAt') && (
        <TableCellOrder>{dayjs(createdAt).format('DD/MM/YYYY')}</TableCellOrder>
      )}
      {showColumn('assignee') && <TableCellOrder>{assignee}</TableCellOrder>}
      <TableCellOrder>{preview}</TableCellOrder>
      {showColumn('channel') && <TableCellOrder>{channel}</TableCellOrder>}
      {showColumn('budget') && <TableCellOrder>{budget} €</TableCellOrder>}
      {showColumn('price') && <TableCellOrder>{price} €</TableCellOrder>}
      {showColumn('runtime') && <TableCellOrder>{runtime}</TableCellOrder>}
      <TableCellOrder action>
        <AdvancedMenu id={id} type={'order'} />
      </TableCellOrder>
    </TableRow>
  )
}

export default OrdersRow

const setPreviewStatus = (status) => {
  switch (status) {
    case 'Waiting for approval':
      return (
        <Tooltip title="Waiting for approval">
          <HourglassTopIcon />
        </Tooltip>
      )
    case 'Approved':
      return (
        <Tooltip title="Approved">
          <CheckIcon color="success" />
        </Tooltip>
      )

    default:
      return (
        <>
          <Tooltip title="Preview uploaded">
            <UploadIcon />
          </Tooltip>
        </>
      )
  }
}
